import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { PALETTE, resolvePalette, type ResolvedGroup } from './palette.ts';
import { ThemePalette } from './ThemePalette.tsx';
import { toHex } from './hex.ts';
import './settings-theme.css';

// The Theme section of Settings: the app's current colour palette. Each
// token's value is read from the document at runtime, on mount and again
// whenever the accordion opens, so the section always shows the palette the
// app is drawing with. A derived token's declared text ("var(--viz-win)",
// "color-mix(…)") is not a colour a swatch can show, so each token is
// resolved through a hidden probe (`color: var(<token>)`, carrying the `viz`
// class like the section), whose computed colour is the real value, shown
// as hex (`toHex`: #rrggbb, #rrggbbaa with alpha); a token that resolves to
// nothing stays ''.
const readPalette = (): ResolvedGroup[] => {
  const root = getComputedStyle(document.documentElement);
  const probe = document.createElement('span');
  probe.className = 'viz';
  probe.setAttribute('aria-hidden', 'true');
  probe.style.display = 'none';
  document.body.appendChild(probe);
  try {
    return resolvePalette((name) => {
      if (!root.getPropertyValue(name).trim()) return '';
      probe.style.color = `var(${name})`;
      return toHex(getComputedStyle(probe).color);
    }, PALETTE);
  } finally {
    probe.remove();
  }
};

export function setup(app: PiletApi) {
  const Section: React.FC = () => {
    const [groups, setGroups] = React.useState<ResolvedGroup[]>(readPalette);
    return <ThemePalette groups={groups} onOpen={() => setGroups(readPalette())} />;
  };

  app.registerExtension('settings.sections', Section, { order: 30 });
}
